import type { AuthUser } from "@/contexts/AuthContext";
import { gqlRequest } from "@/lib/graphql";
import { clearSessionAndRedirectToLogin } from "@/lib/sessionExpiry";

export type ApiLoginResponse = {
  accessToken: string;
  user: AuthUser;
};

export type ApiError = {
  code?: string;
  message?: string;
};

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || "/api").replace(
  /\/$/,
  "",
);

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });

  if (res.status === 401) {
    clearSessionAndRedirectToLogin();
    throw new Error("Session expired");
  }

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (
      data && typeof data === "object" ? (data as ApiError) : {}
    ) as ApiError;
    const message = err.message || `HTTP ${res.status}`;
    const code = err.code;
    const e = new Error(message) as Error & { code?: string; status?: number };
    e.code = code;
    e.status = res.status;
    throw e;
  }

  return data as T;
}

export async function apiLogin(
  email: string,
  password: string,
): Promise<ApiLoginResponse> {
  const variables = { email, password };

  // Try a few common login mutation shapes to be backend-agnostic.
  const attempts: Array<{
    name: string;
    query: string;
    pick: (data: any) => any;
  }> = [
    {
      name: "iamLogin(input:{email,password})",
      query: `
        mutation IamLogin($email: String!, $password: String!) {
          iamLogin(input: { email: $email, password: $password }) {
            accessToken
            user { id email name status roles lastLoginAt createdAt }
          }
        }
      `,
      pick: (data) => data?.iamLogin,
    },
    {
      name: "login(email,password)",
      query: `
        mutation Login($email: String!, $password: String!) {
          login(email: $email, password: $password) {
            accessToken
            user { id name email role avatar }
          }
        }
      `,
      pick: (data) => data?.login,
    },
    {
      name: "login(input:{email,password})",
      query: `
        mutation Login($email: String!, $password: String!) {
          login(input: { email: $email, password: $password }) {
            accessToken
            user { id name email role avatar }
          }
        }
      `,
      pick: (data) => data?.login,
    },
    {
      name: "adminLogin(email,password)",
      query: `
        mutation AdminLogin($email: String!, $password: String!) {
          adminLogin(email: $email, password: $password) {
            accessToken
            user { id name email role avatar }
          }
        }
      `,
      pick: (data) => data?.adminLogin,
    },
    {
      name: "signIn(email,password)",
      query: `
        mutation SignIn($email: String!, $password: String!) {
          signIn(email: $email, password: $password) {
            accessToken
            user { id name email role avatar }
          }
        }
      `,
      pick: (data) => data?.signIn,
    },
  ];

  let lastErr: unknown = null;
  for (const a of attempts) {
    try {
      const data = await gqlRequest(a.query, variables);
      const payload = a.pick(data);
      const accessToken = payload?.accessToken;
      const user = payload?.user;
      if (typeof accessToken === "string" && user && typeof user === "object") {
        const roles = Array.isArray(user.roles)
          ? user.roles.filter((r: unknown) => typeof r === "string")
          : undefined;
        const fromRoles =
          roles && roles.length > 0 ? String(roles[0]).trim() : "";
        const fromField = typeof user.role === "string" ? user.role.trim() : "";
        const role = fromRoles || fromField;
        if (!role) {
          throw new Error("Login failed: missing user role");
        }
        return {
          accessToken,
          user: {
            id: String(user.id),
            email: String(user.email),
            name: String(user.name),
            role,
            roles,
            status: typeof user.status === "string" ? user.status : undefined,
            lastLoginAt:
              typeof user.lastLoginAt === "string"
                ? user.lastLoginAt
                : undefined,
            createdAt:
              typeof user.createdAt === "string" ? user.createdAt : undefined,
          } as AuthUser,
        };
      }
      throw new Error(`Unexpected GraphQL login response shape for ${a.name}`);
    } catch (e) {
      lastErr = e;
      // If the mutation/field doesn't exist, try the next variant.
      const msg = (e as Error)?.message || "";
      const isUnknownField =
        /Cannot query field|Unknown argument|Unknown type|Cannot return null/i.test(
          msg,
        );
      if (isUnknownField) continue;
      throw e;
    }
  }
  throw (lastErr as Error) || new Error("Login failed");
}

export async function apiRequestPasswordReset(
  email: string,
): Promise<{ ok: true }> {
  return await jsonFetch<{ ok: true }>("/v1/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function apiAcceptInvite(params: {
  token: string;
  password: string;
}): Promise<{ ok: true }> {
  return await jsonFetch<{ ok: true }>("/v1/auth/invite/accept", {
    method: "POST",
    body: JSON.stringify(params),
  });
}
